import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card, CardBody, Button, Textarea, Badge, Select, SelectItem, Chip, addToast } from "@heroui/react";
import { Package, DollarSign, Boxes, Calendar, Upload, ImagePlus, FileText, Layers, CheckCircle, RulerIcon, XCircle, TagIcon } from "lucide-react";
import api from "../../api/api";
import { useToast } from "../../context/toast";

// 🎯 Faux images pour la carrousel

export default function ProductCreateForm() {
  const [product, setProduct] = useState({
    name: "",
    description: "",
    price: "",
    stock_quantity: "",
    unity : "kilo",
    image: "",
  });

  const [categories, setCategories] = useState([])
  const [preview, setPreview] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const showToast = useToast()


  const [selectedCategories, setSelectedCategories] = useState([]);

  // 📌 Gérer la sélection des catégories
  const handleSelectionChange = (catId) => {

    setSelectedCategories((prevSelected) =>
      prevSelected.includes(catId)
        ? prevSelected.filter((id) => id !== catId) // Retirer si déjà sélectionné
        : [...prevSelected, catId] // Ajouter si non sélectionné
    );
  };

  // Gestion du changement des inputs
  const handleChange = (e) => {
    setProduct({ ...product, [e.target.name]: e.target.value });
  };


  const handleCreateProduct = async ()=>{

    setIsCreating(true)

    try{

      const formData = new FormData();

        for (const key in product) {
          formData.append(key, product[key]);
        }

        formData.append('category', selectedCategories)

        await api.product.create(formData)

        addToast({
          title: "Success",
          description: "Produit crée avec success",
          color: "success",
        });
        showToast("Produit crée avec success", "success")
        setIsCreating(false)

    }catch(e){

        addToast({
          title: "Erreur",
          description: "Une erreur s'est produite",
          color: "danger",
        });

        setIsCreating(false)
    }

  }

  const handleFileChange = (e) => {
    
    const file = e.target.files[0];

    if (file) {
      
      setProduct({ ...product, [e.target.name]: e.target.files[0] });

      // Création d'un aperçu de l'image
      const reader = new FileReader();
      reader.onloadend = () => setPreview(reader.result);
      reader.readAsDataURL(file);

    }
  };


  useEffect(()=>{

    const fetch = async ()=>{

        try{

            const data = await api.category.read()

            setCategories(data)


        }catch(e){

            console.log(e)

        }
    }

    fetch()

  }, [])

  return (
    <div className="max-w-3xl p-6 mx-auto space-y-6">
      {/* 🏷️ Titre */}
      <h2 className="flex items-center gap-2 text-2xl font-semibold">
        <Package size={24} className="text-primary" /> Créer un Nouveau Produit
      </h2>

      {/* 🎠 Carrousel d'Images */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="flex justify-center items-center">
          <img src={preview} alt="Produit" className="rounded-lg shadow-md w-60 h-60" /> 
      </motion.div>

      {/* 📝 Formulaire */}
      <motion.form initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="space-y-4">
        {/* Nom du produit */}
        <div className="flex flex-col">
          <label className="flex items-center gap-2 font-medium"><FileText size={18} /> Nom du produit</label>
          <input 
            type="text" name="name" placeholder="Nom du produit..." required 
            value={product.name} onChange={handleChange} 
            className="p-2 border rounded-lg shadow-sm focus:outline-primary"
          />
        </div>

        {/* Description */}
        <div className="flex flex-col">
          <label className="flex items-center gap-2 font-medium"><Layers size={18} /> Description</label>
          <textarea 
            name="description" placeholder="Décrivez le produit..." required
            value={product.description} onChange={handleChange}
            className="p-2 border rounded-lg shadow-sm focus:outline-primary"
          />
        </div>

        {/* Prix et Catégorie */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* Prix */}
          <div className="flex flex-col">
            <label className="flex items-center gap-2 font-medium"><DollarSign size={18} /> Prix (€)</label>
            <input 
              type="number" name="price" placeholder="Prix du produit..." required
              value={product.price} onChange={handleChange}
              className="p-2 border rounded-lg shadow-sm focus:outline-primary"
            />
          </div>


          <div className="flex flex-col">
            <label className="flex items-center gap-2 font-medium"><RulerIcon size={18} /> Unité</label>
            <select
              name="unity"
              value={product.unity}
              onChange={handleChange}
              className="p-2 border rounded-lg shadow-sm focus:outline-primary bg-white dark:bg-gray-800"
            >
              <option value="" disabled>-- Sélectionnez une unité --</option>
                <option value={'kilo'}>{'Kilogramme'}</option>
                <option value={'gramme'}>{'gramme'}</option>
            </select>
          </div>

          {/* Catégorie */}
          <div className="flex flex-col">
            <label className="flex items-center gap-2 font-medium"><Boxes size={18} /> Catégorie</label>
            <div className="py-5 space-x-4 space-y-4">
              {categories.map((cat) => (
                <Button
                  key={cat.id}
                  startContent={<TagIcon className="w-4 h-4"/>}
                  size="sm"
                  onPress={() => handleSelectionChange(cat.id)}
                  className={`transition-colors  ${
                    selectedCategories.includes(cat.id) ? "bg-green-600 text-white" : "bg-gray-200 dark:bg-gray-800"
                  }`}
                >
                  {cat.name}
                </Button>
              ))}
          </div>
          </div>
        </div>


        {/* Stock */}
        <div className="flex flex-col">
          <label className="flex items-center gap-2 font-medium"><CheckCircle size={18} /> Quantité en Stock</label>
          <input 
            type="number" name="stock_quantity" placeholder="Stock disponible..." required
            value={product.stock_quantity} onChange={handleChange}
            className="p-2 border rounded-lg shadow-sm focus:outline-primary"
          />
        </div>

        {/* Upload Image */}
        <div className="flex flex-col">
          <label className="flex items-center gap-2 font-medium"><Upload size={18} /> Image du Produit</label>
          <input type="file" accept="image/*" name='image' onChange={handleFileChange} className="p-2 border rounded-lg shadow-sm focus:outline-primary" />
        </div>


        {/* Bouton de soumission */}
        <Button 
          color="primary" 
          fullWidth 
          onPress={handleCreateProduct} 
          isLoading={isCreating}
        >
          Ajouter le Produit
        </Button>
      </motion.form>
    </div>
  );
}
